import * as React from 'react';
import {connect} from 'react-redux';
import {Link as RoutedLink} from 'react-router-dom';

import {Assignables} from 'nutshell-core/assignables';
import {
    DUE_UNIT_HOURS_ID,
    DUE_UNIT_DAYS_ID,
    DUE_UNIT_BUSINESS_DAYS_ID,
    DUE_UNIT_MINUTES_ID,
} from 'nutshell-core/date-time/delays';
import type {User, DueDateUnit as DelayUnit} from 'nutshell-core/types';

import {AssigneeSelect} from 'shells/assignee-select/assignee-select';
import {DelayInput} from 'shells/delay-bar/delay-input';
import {CrossBrowserHiddenFormSubmit} from 'shells/form/cross-browser-hidden-form-submit';
import {FormField} from 'shells/form/form-field';
import {Label} from 'shells/form/label';
import {Link} from 'shells/link/link';
import {PopoverInfo} from 'shells/popover-info/popover-info';

import type {Flow} from 'flow-to-typescript-codemod';
import type {
    CreateEmailAutomationInputFieldInput,
    DeleteEmailAutomationInputFieldInput,
    GetEmailAutomation_emailAutomation,
    StageBasic as StageBasicFragment,
    UpdateEmailAutomationInputFieldInput,
    EmailSequenceTemplateBasic as EmailSequenceTemplate,
} from 'nutshell-graphql-types';

import {GraphQLSelectSalesAutomationableEmailSequenceTemplates} from '../../../core/select/graphql-select-sales-automationable-email-sequence-templates';
import {SalesAutomationSidebarFormWrapper} from '../../../core/sidebar/sales-automation-sidebar-form-wrapper';

import type {NutshellState} from '../../../../store';

import {EmailAutomationSelect, SEND_UNTIL_LEAD_CLOSE_MAGIC_ID} from './email-automation-select';

import styles from '../sales-process-form.module.css';

type OwnProps = {
    stagesetId: string;
    stageId: string;
    isFetchingLatestVersion: boolean;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => any;
    emailAutomation: GetEmailAutomation_emailAutomation | null | undefined;
    createEmailAutomation: (
        // We don't want to force the type value here, because that's set in
        // form-emails.js. But, this is better than a flow ignore.
        arg1: Flow.Diff<
            CreateEmailAutomationInputFieldInput,
            {
                type: string;
            }
        >
    ) => Promise<any>;
    updateEmailAutomation: (arg1: UpdateEmailAutomationInputFieldInput) => Promise<any>;
    deleteEmailAutomation: (arg1: DeleteEmailAutomationInputFieldInput) => Promise<any>;
};

type StateProps = {
    users: User[];
};

type Props = OwnProps & StateProps;

function mapStateToProps(state: NutshellState): StateProps {
    return {
        // @ts-expect-error
        users: Assignables.getAllEnabledUsers(state),
    };
}

const DEFAULT_DELAY_VALUE = 30;
const DEFAULT_DELAY_UNIT = DUE_UNIT_MINUTES_ID;

type State = {
    selectedKeepUntilStageId: string | null | undefined;
    selectedEmailSequence: EmailSequenceTemplate | null | undefined;
    sendFromUserId: string | null | undefined;
    delayValue: number;
    delayUnit: DelayUnit;
};

const DEFAULT_ASSIGNEE_ID = 'OWNER';
const LEAD_ASSIGNEE_OPTION = {id: DEFAULT_ASSIGNEE_ID, name: 'Lead assignee'} as const;

function getDelayValueFromEmailAutomation(
    emailAutomation?: GetEmailAutomation_emailAutomation | null
): number {
    const delayValue = emailAutomation && emailAutomation.delay && emailAutomation.delay.timespan;
    if (!delayValue) return DEFAULT_DELAY_VALUE;

    return delayValue;
}

function getDelayUnitFromEmailAutomation(
    emailAutomation?: GetEmailAutomation_emailAutomation | null
): DelayUnit {
    const delayUnit = emailAutomation && emailAutomation.delay && emailAutomation.delay.unit;
    if (!delayUnit) return DEFAULT_DELAY_UNIT;

    return delayUnit;
}

function makeEmailAutomationDelayFromDelayOptions(delayValue: number, delayUnit: DelayUnit) {
    return {
        unit: delayUnit,
        timespan: delayValue,
    };
}

function validateDelayOptions(delayValue: number, delayUnit: DelayUnit): boolean {
    if (delayUnit === 'mm' && delayValue < 5) {
        return false;
    }

    return true;
}

export class FormNutshellEmailSequenceComponentComponent extends React.PureComponent<Props, State> {
    // @ts-expect-error
    emailSequenceSelectFieldRef: HTMLInputElement;
    helpButtonRef: HTMLButtonElement | null | undefined;

    constructor(props: Props) {
        super(props);

        const {emailAutomation} = props;

        if (emailAutomation) {
            // Note: gotta update UNSAFE_componentWillReceiveProps too, if you change this
            this.state = {
                selectedKeepUntilStageId: this.getExitsStageValueForState(
                    emailAutomation.exitsStage
                ),
                selectedEmailSequence:
                    emailAutomation.emailList &&
                    emailAutomation.emailList.__typename === 'EmailSequenceTemplate'
                        ? emailAutomation.emailList
                        : null,
                sendFromUserId: emailAutomation.sendFromUser
                    ? emailAutomation.sendFromUser.id
                    : DEFAULT_ASSIGNEE_ID,
                delayValue: getDelayValueFromEmailAutomation(emailAutomation),
                delayUnit: getDelayUnitFromEmailAutomation(emailAutomation),
            };
        } else {
            this.state = {
                selectedKeepUntilStageId: SEND_UNTIL_LEAD_CLOSE_MAGIC_ID,
                selectedEmailSequence: null,
                sendFromUserId: DEFAULT_ASSIGNEE_ID,
                delayValue: DEFAULT_DELAY_VALUE,
                delayUnit: DEFAULT_DELAY_UNIT,
            };
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const {emailAutomation} = nextProps;

        if (emailAutomation && !this.props.emailAutomation) {
            this.setState({
                selectedKeepUntilStageId: this.getExitsStageValueForState(
                    emailAutomation.exitsStage
                ),
                selectedEmailSequence:
                    emailAutomation.emailList &&
                    emailAutomation.emailList.__typename === 'EmailSequenceTemplate'
                        ? emailAutomation.emailList
                        : null,
                sendFromUserId: emailAutomation.sendFromUser
                    ? emailAutomation.sendFromUser.id
                    : DEFAULT_ASSIGNEE_ID,
                delayValue: getDelayValueFromEmailAutomation(emailAutomation),
                delayUnit: getDelayUnitFromEmailAutomation(emailAutomation),
            });
        }
    }

    render() {
        const emailList = this.props.emailAutomation && this.props.emailAutomation.emailList;
        const sequenceTemplate =
            emailList && emailList.__typename === 'EmailSequenceTemplate' ? emailList : null;

        return (
            <SalesAutomationSidebarFormWrapper
                id={this.props.emailAutomation ? this.props.emailAutomation.id : null}
                isFetchingLatestVersion={this.props.isFetchingLatestVersion}
                onSaveForm={this.handleSaveForm}
                onDelete={{
                    deleteText: 'Remove this automation',
                    deleteConfirmationTitle:
                        'Are you sure you want to delete this email automation?',
                    onDeleteConfirm: this.handleDeleteConfirm,
                }}
            >
                <div
                    className={
                        this.state.selectedEmailSequence
                            ? styles['subheader--with-divider']
                            : styles.subheader
                    }
                >
                    When a lead enters this stage, automatically start sending personal emails.
                </div>
                {this.state.selectedEmailSequence ? (
                    <div className={styles['email-sequence-template-link']}>
                        <div>Sending:</div>
                        <Link
                            id='selected-email-sequence'
                            // @ts-expect-error
                            as={RoutedLink}
                            newTab={true}
                            variant='primary'
                            to={this.state.selectedEmailSequence.htmlUrlPath}
                        >
                            {this.state.selectedEmailSequence.name}
                        </Link>
                    </div>
                ) : undefined}
                <div className='flex align-start'>
                    <Label htmlFor='email-sequence-select'>Send an email</Label>
                    <PopoverInfo info='Missing a template?  Only shared templates and templates without custom placeholders can be automated.' />
                </div>
                <FormField innerId='email-sequence-select'>
                    <GraphQLSelectSalesAutomationableEmailSequenceTemplates
                        inputId='email-sequence-select'
                        stagesetId={this.props.stagesetId}
                        onClearValue={() => this.setState({selectedEmailSequence: undefined})}
                        onChange={this.handleAddToListChange}
                        selectedId={
                            this.state.selectedEmailSequence
                                ? this.state.selectedEmailSequence.id
                                : undefined
                        }
                        currentTemplate={sequenceTemplate}
                    />
                </FormField>
                <Label htmlFor='delay-config'>Start sending…</Label>
                <FormField
                    innerId='delay-config'
                    errorMessage={
                        !validateDelayOptions(this.state.delayValue, this.state.delayUnit)
                            ? 'Delays need to be 5 minutes or longer'
                            : undefined
                    }
                >
                    <div>
                        <div className={styles['delay-container']}>
                            <DelayInput
                                customOptions={[
                                    {
                                        label: this.state.delayValue === 1 ? 'Minute' : 'Minutes',
                                        value: DUE_UNIT_MINUTES_ID,
                                    },
                                    {
                                        label: this.state.delayValue === 1 ? 'Hour' : 'Hours',
                                        value: DUE_UNIT_HOURS_ID,
                                    },
                                    {
                                        label: this.state.delayValue === 1 ? 'Day' : 'Days',
                                        value: DUE_UNIT_DAYS_ID,
                                    },
                                    {
                                        label:
                                            this.state.delayValue === 1
                                                ? 'Business day'
                                                : 'Business days',
                                        value: DUE_UNIT_BUSINESS_DAYS_ID,
                                    },
                                ]}
                                inputValue={this.state.delayValue}
                                selectedOption={this.state.delayUnit}
                                onInputChange={(newValue) => {
                                    this.setState({delayValue: newValue});
                                }}
                                onSelectChange={(newUnit) => {
                                    this.setState({delayUnit: newUnit});
                                }}
                            />
                        </div>
                        <p className={styles['delay-description']}>after the lead enters stage</p>
                    </div>
                </FormField>
                <div className='flex align-start'>
                    <Label htmlFor='exit-stage'>Stop sending emails after…</Label>
                    <PopoverInfo info='For email sequences with more than one email, continue sending emails following the schedule defined on the sequence until receiving a reply or the lead leaves this stage.' />{' '}
                </div>
                <FormField innerId='exit-stage'>
                    <EmailAutomationSelect
                        inputId='exit-stage'
                        stageId={this.props.stageId}
                        stagesetId={this.props.stagesetId}
                        onClearValue={
                            this.state.selectedKeepUntilStageId !== SEND_UNTIL_LEAD_CLOSE_MAGIC_ID
                                ? () => {
                                      this.setState({
                                          selectedKeepUntilStageId: SEND_UNTIL_LEAD_CLOSE_MAGIC_ID,
                                      });
                                  }
                                : undefined
                        }
                        onChange={this.handleKeepUntilChange}
                        selectedId={this.state.selectedKeepUntilStageId}
                        includeUntilClose={true}
                    />
                </FormField>
                <Label htmlFor='email-sender'>Send email from…</Label>
                <FormField innerId='email-sender'>
                    <AssigneeSelect
                        value={this.state.sendFromUserId}
                        inputId='email-sender'
                        options={this.props.users}
                        customOptions={[LEAD_ASSIGNEE_OPTION]}
                        isClearable={this.state.sendFromUserId !== DEFAULT_ASSIGNEE_ID}
                        onChange={this.handleSenderChange}
                    />
                </FormField>
                <CrossBrowserHiddenFormSubmit inputRef={this.props.formSubmitBtnRef} />
            </SalesAutomationSidebarFormWrapper>
        );
    }

    handleAddToListChange = (newValue: EmailSequenceTemplate) => {
        this.setState({selectedEmailSequence: newValue});
    };

    handleKeepUntilChange = (stage: StageBasicFragment) => {
        this.setState({selectedKeepUntilStageId: stage.id});
    };

    handleSenderChange = (sendFromUserId?: string | null) => {
        this.setState({sendFromUserId: sendFromUserId || DEFAULT_ASSIGNEE_ID});
    };

    handleDeleteConfirm = (emailAutomationId: string) => {
        return this.props.deleteEmailAutomation({
            id: emailAutomationId,
        });
    };

    handleSaveForm = () => {
        const sendUntilLeadClose =
            this.state.selectedKeepUntilStageId === SEND_UNTIL_LEAD_CLOSE_MAGIC_ID;

        const delayIsValid = validateDelayOptions(this.state.delayValue, this.state.delayUnit);

        if (
            this.props.emailAutomation &&
            this.state.selectedKeepUntilStageId &&
            this.state.selectedEmailSequence
        ) {
            if (!delayIsValid) return;

            return this.props.updateEmailAutomation({
                id: this.props.emailAutomation.id,
                entersStageId: this.props.stageId,
                emailListId: this.state.selectedEmailSequence.id,
                exitsStageId: this.sanitizeExitsStageValueBeforeSave(
                    this.state.selectedKeepUntilStageId
                ),
                sendUntilClose: sendUntilLeadClose,
                sendFromUserId:
                    this.state.sendFromUserId === DEFAULT_ASSIGNEE_ID
                        ? null
                        : this.state.sendFromUserId,
                delay: makeEmailAutomationDelayFromDelayOptions(
                    this.state.delayValue,
                    this.state.delayUnit
                ),
            });
        } else if (this.state.selectedKeepUntilStageId && this.state.selectedEmailSequence) {
            if (!delayIsValid) return;

            return this.props.createEmailAutomation({
                entersStageId: this.props.stageId,
                emailListId: this.state.selectedEmailSequence.id,
                exitsStageId: this.sanitizeExitsStageValueBeforeSave(
                    this.state.selectedKeepUntilStageId
                ),
                sendUntilClose: sendUntilLeadClose,
                sendFromUserId:
                    this.state.sendFromUserId === DEFAULT_ASSIGNEE_ID
                        ? null
                        : this.state.sendFromUserId,
                delay: makeEmailAutomationDelayFromDelayOptions(
                    this.state.delayValue,
                    this.state.delayUnit
                ),
            });
        }
    };

    /**
     * Takes our current state's "exits" id value, and translates that into
     * what should actually be passed to the server.
     *
     * Specifically, this means that if our currnet "exits" id value is the
     * magic string SEND_UNTIL_LEAD_CLOSE_MAGIC_ID, we'll pass `null`, since that's
     * how it's represented on the server
     *
     * @param  {string} value         Current id in our state
     * @return {?string}              Nullable id to pass to server
     */
    sanitizeExitsStageValueBeforeSave = (value: string) => {
        return value === SEND_UNTIL_LEAD_CLOSE_MAGIC_ID ? null : value;
    };

    /**
     * Translates a potentially null `exitsStage` value into the proper
     * id for us to seed our state with.
     *
     * If the exitsStage value passed is null, we know that our state should
     * be seeded with the magic "Forever - never remove from list" id.
     *
     * @param  {Object} [value]   - Nullable stage fragment
     * @return {string}           - stageId | magic "FOREVER" id
     */
    getExitsStageValueForState = (value?: StageBasicFragment | null) => {
        return value ? value.id : SEND_UNTIL_LEAD_CLOSE_MAGIC_ID;
    };
}

// @ts-expect-error
export const FormNutshellEmailSequenceComponent = connect<Props, OwnProps, _, _, _, _>(
    mapStateToProps
)(FormNutshellEmailSequenceComponentComponent);
